import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppStore } from '../stores/useAppStore'
import { jsaService, type JSAData } from '../services/jsaService'
import SignaturePad from './SignaturePad'
import { config, apiEndpoints } from '../config/env'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Checkbox } from './ui/checkbox'
import { 
  TruckIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentCheckIcon,
  PencilIcon
} from '@heroicons/react/24/outline'

const JSATravel: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, addNotification } = useAppStore()
  
  const [jsaData, setJsaData] = useState<JSAData>(() => {
    const techId = user?.technicianId || Number(user?.id) || 0    
    return jsaService.developmentFallback(techId, user?.name || 'Unknown')
  })
  
  const [signatureBase64, setSignatureBase64] = useState<string>('')
  const [existingSignatureUrl, setExistingSignatureUrl] = useState<string>('')
  const [isCompleted, setIsCompleted] = useState(false)

  const technicianId = user?.technicianId || Number(user?.id) || 0
  const today = new Date().toLocaleString('sv').split(' ')[0]

  // Load existing JSA data
  const { 
    data: existingJSA,
    isLoading: loadingJSA,
    error: jsaError 
  } = useQuery({
    queryKey: ['jsa', technicianId, today],
    queryFn: () => jsaService.getJSAByTechAndDate(technicianId, today),
    enabled: !!technicianId,
    retry: false
  })

  // Handle JSA data when loaded
  useEffect(() => {
    if (existingJSA) {
      setJsaData(existingJSA)
      setIsCompleted(!!existingJSA.jsaSignature)
      
      // Set existing signature URL if available
      if (existingJSA.jsaSignature) {
        setExistingSignatureUrl(apiEndpoints.files.serve(existingJSA.jsaSignature))
      }
    }
  }, [existingJSA])

  // Handle JSA loading error
  useEffect(() => {
    if (jsaError) {
    }
  }, [jsaError])

  // Save JSA mutation
  const saveJSAMutation = useMutation({
    mutationFn: async (data: { jsaData: JSAData; signature?: string }) => {      
      const result = await jsaService.createJSA(data.jsaData)

      if (data.signature && data.signature.length > 0) {
        const signature = await jsaService.saveJSASignature({
          signatureBase64: data.signature,
          techID_FK: data.jsaData.techID_FK,
          jsaDate: data.jsaData.jsaDate
        })

        data.jsaData.jsaSignature = signature.fileName;
      }

      return result
    },
    onSuccess: () => {
      // Save signature to localStorage for future use
      if (signatureBase64) {
        jsaService.saveSignatureToStorage(signatureBase64)
      } else {
      }
      
      addNotification({
        type: 'success',
        message: 'Travel JSA saved successfully!',
        timestamp: Date.now()
      })
      queryClient.invalidateQueries({ queryKey: ['jsa', technicianId, today] })
      navigate('/')
    },
    onError: (error: Error) => {
      console.error('Failed to save JSA:', error)
      addNotification({
        type: 'error',
        message: 'Failed to save Travel JSA. Please try again.',
        timestamp: Date.now()
      })
    }
  })

  // Update JSA data
  const updateJSAData = (updates: Partial<JSAData>) => {
    setJsaData(prev => ({ ...prev, ...updates }))
  }

  // Handle radio button changes
  const handleConfirmationChange = (value: string) => {
    updateJSAData({ confirmation: value === 'true' })
  }

  const handleActionChange = (value: string) => {
    updateJSAData({ action: value === 'true' })
  }

  // Handle signature change
  const handleSignatureChange = (signature: string) => {
    setSignatureBase64(signature)
  }

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (jsaData.confirmation !== true) {
      addNotification({
        type: 'error',
        message: 'You must confirm that you have considered the JSA and implemented actions to minimise risks',
        timestamp: Date.now()
      })
      return
    }

    if (jsaData.action === undefined || jsaData.action === null) {
      addNotification({
        type: 'error',
        message: 'You must answer the action required question',
        timestamp: Date.now()
      })
      return
    }

    if (jsaData.action && (!jsaData.jobStep1 && !jsaData.hazard1 && !jsaData.action1 && !jsaData.who1)) {
      addNotification({
        type: 'error',
        message: 'You must fill in at least one action required row',
        timestamp: Date.now()
      })
      return
    }

    if (!signatureBase64 && !isCompleted) {
      addNotification({
        type: 'error',
        message: 'You must provide a signature to continue',
        timestamp: Date.now()
      })
      return
    }

    // Save JSA
    saveJSAMutation.mutate({
      jsaData: jsaData,
      signature: signatureBase64
    })
  }

  const handleBackToDashboard = () => {
    navigate('/')
  }

  if (!user || !technicianId) {
    return (
      <div className="container-mobile py-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500" />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">Authentication Required</h3>
              <p className="text-gray-600 mt-1">Please log in to access the JSA Travel form.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (loadingJSA) {
    return (
      <div className="container-mobile py-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-500">Loading Travel JSA...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
          <TruckIcon className="h-8 w-8 text-blue-600" />
          Travel JSA
        </h1>
        <p className="text-gray-600 mt-1">Job Safety Analysis for Travel</p>
        {config.isDevelopment && (
          <Badge variant="outline" className="mt-2">
            Development Mode - API: {config.apiBaseUrl}
          </Badge>
        )}
      </div>

      {/* Completion Status */}
      {isCompleted && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
              <span className="text-green-800 font-medium">Travel JSA Completed</span>
            </div>
            <Button 
              variant="outline"
              size="sm"
              onClick={handleBackToDashboard}
              className="border-green-300 text-green-700 hover:bg-green-100"
            >
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Standard JSA Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DocumentCheckIcon className="h-5 w-5" />
            Standard JSA for Travel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-semibold">Job step</th>
                  <th className="text-left p-3 font-semibold">Hazard/s in this step</th>
                  <th className="text-left p-3 font-semibold">What action are we going to take to minimise this risk</th>
                  <th className="text-left p-3 font-semibold">Who will take this action</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3 align-top font-medium">Travel to site</td>
                  <td className="p-3 align-top">
                    <ol className="list-decimal list-inside space-y-1 text-sm">
                      <li>Unsafe access to entry</li>
                      <li>Potentially dangerous animals</li>
                      <li>Unaccompanied minors on site</li>
                      <li>Aggressive occupants</li>
                    </ol>
                  </td>
                  <td className="p-3 align-top">
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Send entry notices inc safe access</li>
                      <li>If animals are uncontrolled and appear aggressive DO NOT ENTER (call office)</li>
                      <li>If unaccompanied minors on site DO NOT ENTER (call office)</li>
                      <li>If aggressive occupants DO NOT ENTER (call office)</li>
                    </ul>
                  </td>
                  <td className="p-3 align-top text-sm">
                    Admin Staff<br />
                    Electricians and Technicians
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Statement */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5" />
            Confirmation Statement
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-700">
            I have considered the above standard JSA for travel and have implemented the actions to minimise the risks?
          </p>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="confirmation-yes"
              checked={jsaData.confirmation === true}
              onCheckedChange={() => handleConfirmationChange('true')}
              disabled={isCompleted}
            />
            <Label htmlFor="confirmation-yes" className="text-sm font-medium">
              Yes, I confirm
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Action Required */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5" />
            Action Required
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-700">
            Are there any additional hazards NOT included in the standard JSA?
          </p>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="action-yes"
                checked={jsaData.action === true}
                onCheckedChange={() => handleActionChange('true')}
                disabled={isCompleted}
              />
              <Label htmlFor="action-yes" className="text-sm font-medium">
                Yes, additional hazards identified
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="action-no"
                checked={jsaData.action === false}
                onCheckedChange={() => handleActionChange('false')}
                disabled={isCompleted}
              />
              <Label htmlFor="action-no" className="text-sm font-medium">
                No, no additional hazards
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Actions Table */}
      {jsaData.action && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PencilIcon className="h-5 w-5" />
              Additional Actions Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700 text-sm">
              Document the actions you have taken to minimise additional risks:
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-semibold text-sm">Job step</th>
                    <th className="text-left p-3 font-semibold text-sm">Hazard/s in this step</th>
                    <th className="text-left p-3 font-semibold text-sm">What action are we going to take to minimise this risk</th>
                    <th className="text-left p-3 font-semibold text-sm">Who will take this action</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3].map((row) => (
                    <tr key={row} className="border-b">
                      <td className="p-2">
                        <Input
                          value={jsaData[`jobStep${row}` as keyof JSAData] as string || ''}
                          onChange={(e) => updateJSAData({ [`jobStep${row}`]: e.target.value } as Partial<JSAData>)}
                          disabled={isCompleted}
                          placeholder="Enter job step"
                          className="min-w-0"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={jsaData[`hazard${row}` as keyof JSAData] as string || ''}
                          onChange={(e) => updateJSAData({ [`hazard${row}`]: e.target.value } as Partial<JSAData>)}
                          disabled={isCompleted}
                          placeholder="Enter hazard"
                          className="min-w-0"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={jsaData[`action${row}` as keyof JSAData] as string || ''}
                          onChange={(e) => updateJSAData({ [`action${row}`]: e.target.value } as Partial<JSAData>)}
                          disabled={isCompleted}
                          placeholder="Enter action"
                          className="min-w-0"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={jsaData[`who${row}` as keyof JSAData] as string || ''}
                          onChange={(e) => updateJSAData({ [`who${row}`]: e.target.value } as Partial<JSAData>)}
                          disabled={isCompleted}
                          placeholder="Enter who"
                          className="min-w-0"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signature Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PencilIcon className="h-5 w-5" />
            Completion & Signature
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Technician Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="technician-name">Completed By</Label>
              <Input
                id="technician-name"
                value={jsaData.technicianName || ''}
                onChange={(e) => updateJSAData({ technicianName: e.target.value })}
                disabled={isCompleted}
                placeholder="Enter technician name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="jsa-date">Date</Label>
              <Input
                id="jsa-date"
                value={new Date(jsaData.jsaDate).toLocaleDateString('en-AU')}
                disabled
              />
            </div>
          </div>

          {/* Signature Area */}
          <div className="space-y-4">
            {!isCompleted ? (
              <div className="space-y-3">
                <Label>Signature</Label>
                <div className="border rounded-lg p-4 bg-white">
                  <SignaturePad
                    onSignatureChange={handleSignatureChange}
                    width={400}
                    height={200}
                    initialSignature={(() => {
                      const stored = jsaService.getSignatureFromStorage()
                      return stored
                    })()}
                  />
                </div>
              </div>
            ) : (
              existingSignatureUrl && (
                <div className="space-y-3">
                  <Label>Signature</Label>
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <img 
                      src={existingSignatureUrl} 
                      alt="JSA Signature" 
                      className="max-w-full h-auto border border-gray-200 rounded"
                    />
                  </div>
                </div>
              )
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleBackToDashboard}
              className="flex-1 sm:flex-none"
            >
              Back to Dashboard
            </Button>
            
            {!isCompleted && (
              <Button
                onClick={handleSubmit}
                disabled={saveJSAMutation.isPending}
                className="flex-1 sm:flex-none"
              >
                {saveJSAMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Save & Submit JSA'
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default JSATravel 